import { useFetchCaseTasks } from "@/queries/useFetchCaseTasks.ts";
import { type ReactNode, createContext, useContext, useMemo } from "react";

import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import type { CaseTaskWithDueDate } from "@/types/caseTask.ts";

type CaseTaskContextType = {
    caseTasksPending: CaseTaskWithDueDate[];
    caseTasksAll: CaseTaskWithDueDate[];
    isLoading: boolean;
    error: Error | null;
};

const CaseTaskContext = createContext<CaseTaskContextType | undefined>(undefined);

type Props = {
    idUUID: string;
    children: ReactNode;
};

const CaseTaskProvider = ({ idUUID, children }: Props) => {
    const { employee, hasPermission } = useEmployee();

    const { data, isLoading, error } = useFetchCaseTasks({
        idUUID: idUUID,
    });

    const value = useMemo(() => {
        const withDates =
            data?.data.map((task) => {
                // For tasks with duration = 0, use a far future date for sorting purposes
                const dueDate =
                    task.duration === 0
                        ? new Date(9999, 11, 31) // Far future date for tasks without due dates
                        : new Date(
                              task.metaCreatedWhen.getTime() + task.duration * 24 * 60 * 60 * 1000,
                          );
                return {
                    ...task,
                    dueDate,
                    overdue: task.duration === 0 ? false : dueDate < new Date(),
                };
            }) ?? [];

        return {
            caseTasksPending: withDates
                .filter((caseTask) => caseTask.status === "Pending")
                .filter((caseTask) => {
                    if (hasPermission("complete-for-another:case-task")) {
                        return true;
                    }

                    return caseTask.coreRoles.includes(employee?.dashboardVersion ?? "");
                }),
            caseTasksAll: withDates,
            isLoading,
            error,
        };
    }, [isLoading, data, error, employee?.dashboardVersion, hasPermission]);

    return <CaseTaskContext.Provider value={value}>{children}</CaseTaskContext.Provider>;
};

export const useCaseTask = () => {
    const context = useContext(CaseTaskContext);

    if (context === undefined) {
        throw new Error("useCaseTask must be used within an CaseTaskProvider");
    }

    return context;
};

export default CaseTaskProvider;
